import dayjs from 'dayjs';
import { DatabaseIdGenerator } from '../utility/database-id-generator.js';
import { SerializationException } from '../exceptions/serialization-exception.js';
import { BinaryReader } from './binary-reader.js';
import { BinaryWriter } from './binary-writer.js';
import {
  TemporarySeatSelectionEntry, PermanentSeatSelectionEntry, ISeatSelectionEntry,
  ReservedSeatSelectionEntry, PassSeatSelectionEntry,
  ArtificialSeatSelectionEntry,
} from '../models/serializable/ticketing-state/seat-selection-entry.js';

interface SeatSelectionFeatures {
  passLocks?: boolean,
  expirationDate?: boolean
}

export class SeatSelectionSerializer {
  private static TEMPORARY_SEAT_SELECTION_ENTRY_TYPE_ID: number = 0;
  private static PERMANENT_SEAT_SELECTION_ENTRY_TYPE_ID: number = 1;
  private static RESERVED_SEAT_SELECTION_ENTRY_TYPE_ID: number = 2;
  private static PASS_SEAT_SELECTION_ENTRY_TYPE_ID: number = 3;
  private static ARTIFICIAL_SEAT_SELECTION_ENTRY_TYPE_ID: number = 4;

  static serialize(entries: ISeatSelectionEntry[]): Uint8Array {
    return this.asBytes(entries);
  }

  static deserialize(bytes: Uint8Array): ISeatSelectionEntry[] {
    const reader = BinaryReader.fromBytes(bytes);
    const format = reader.readByte();

    switch(format) {
      case 1:
        return this.fromBytes_v1(reader);
      case 2:
        return this.fromBytes_v2(reader);
      case 3:
        return this.fromBytes_v3(reader);
      case 4:
        return this.fromBytes(reader);
    }

    throw new SerializationException(`Seat selection cannot be deserialized because format ${format} is unknown`);
  }

  private static asBytes(entries: ISeatSelectionEntry[]): Uint8Array {
    const writer = new BinaryWriter();
    let version: number = 2;

    // TODO: Remove once everything is deployed!
    for(let i = 0; i < entries.length; i++) {
      const entry = entries[i];
      if(PassSeatSelectionEntry.assertType(entry) && entry.locks.length > 0) {
        version = Math.max(version, 3);
      }
      if(ReservedSeatSelectionEntry.assertType(entry) && entry.expirationDate != null) {
        version = Math.max(version, 4);
      }
    }

    const features: SeatSelectionFeatures = {
      passLocks: version >= 3,
      expirationDate: version >= 4,
    };

    writer.writeByte(version);
    writer.writeInt32(entries.length);

    for(let i = 0; i < entries.length; i++) {
      this.writeSeatSelectionEntry(writer, entries[i], features);
    }

    return writer.toBytes();
  }

  static writeSeatSelectionEntry(writer: BinaryWriter, entry: ISeatSelectionEntry, features: SeatSelectionFeatures): void {
    if(entry instanceof TemporarySeatSelectionEntry) {
      this.writeTemporarySeatSelectionEntry(writer, entry, features);
    }
    else if(entry instanceof PermanentSeatSelectionEntry) {
      this.writePermanentSeatSelectionEntry(writer, entry, features);
    }
    else if(entry instanceof ReservedSeatSelectionEntry) {
      this.writeReservedSeatSelectionEntry(writer, entry, features);
    }
    else if(entry instanceof PassSeatSelectionEntry) {
      this.writePassSeatSelectionEntry(writer, entry, features);
    }
    else if(entry instanceof ArtificialSeatSelectionEntry) {
      this.writeArtificialSeatSelectionEntry(writer, entry, features);
    }
    else {
      throw new SerializationException('Seat selection entry cannot be serialized because type is unknown');
    }
  }

  static writeTemporarySeatSelectionEntry(writer: BinaryWriter, entry: TemporarySeatSelectionEntry, features: SeatSelectionFeatures): void {
    writer.writeByte(SeatSelectionSerializer.TEMPORARY_SEAT_SELECTION_ENTRY_TYPE_ID);
    this.writeUniqueId(writer, entry.sessionId, DatabaseIdGenerator.kAuthIdLength);
    writer.writeInt32(entry.creationDate.unix());
    writer.writeInt32(entry.expirationDate.unix());
    writer.writeInt32(entry.seats.length);

    for(let i = 0; i < entry.seats.length; i++) {
      writer.writeInt32(entry.seats[i]);
    }
  }

  static writePermanentSeatSelectionEntry(writer: BinaryWriter, entry: PermanentSeatSelectionEntry, features: SeatSelectionFeatures): void {
    writer.writeByte(SeatSelectionSerializer.PERMANENT_SEAT_SELECTION_ENTRY_TYPE_ID);
    this.writeUniqueId(writer, entry.sessionId, DatabaseIdGenerator.kAuthIdLength);
    writer.writeInt32(entry.creationDate.unix());
    writer.writeInt32(entry.seats.length);

    for(let i = 0; i < entry.seats.length; i++) {
      writer.writeInt32(entry.seats[i]);
    }
  }

  static writeReservedSeatSelectionEntry(writer: BinaryWriter, entry: ReservedSeatSelectionEntry, features: SeatSelectionFeatures): void {
    writer.writeByte(SeatSelectionSerializer.RESERVED_SEAT_SELECTION_ENTRY_TYPE_ID);
    this.writeUniqueId(writer, entry.sessionId, DatabaseIdGenerator.kAuthIdLength);
    this.writeUniqueId(writer, entry.reservationId, DatabaseIdGenerator.kAuthIdLength);
    writer.writeInt32(entry.creationDate.unix());

    if(features.expirationDate === true) {
      writer.writeInt32(entry.expirationDate != null ? entry.expirationDate.unix() : 0);
    }

    writer.writeInt32(entry.seats.length);

    for(let i = 0; i < entry.seats.length; i++) {
      writer.writeInt32(entry.seats[i]);
    }
  }

  static writePassSeatSelectionEntry(writer: BinaryWriter, entry: PassSeatSelectionEntry, features: SeatSelectionFeatures): void {
    writer.writeByte(SeatSelectionSerializer.PASS_SEAT_SELECTION_ENTRY_TYPE_ID);

    this.writeUniqueId(writer, entry.templateId, DatabaseIdGenerator.kAuthIdLength);
    writer.writeInt32(entry.creationDate.unix());
    writer.writeInt32(entry.seats.length);

    for(let i = 0; i < entry.seats.length; i++) {
      writer.writeInt32(entry.seats[i]);
    }

    if(features.passLocks) {
      writer.writeInt32(entry.locks.length);

      for(let i = 0; i < entry.locks.length; i++) {
        writer.writeInt32(entry.locks[i]);
      }
    }
  }

  static writeArtificialSeatSelectionEntry(writer: BinaryWriter, entry: ArtificialSeatSelectionEntry, features: SeatSelectionFeatures): void {
    writer.writeByte(SeatSelectionSerializer.ARTIFICIAL_SEAT_SELECTION_ENTRY_TYPE_ID);

    writer.writeInt32(entry.creationDate.unix());
    writer.writeInt32(entry.seats.length);

    for(let i = 0; i < entry.seats.length; i++) {
      writer.writeInt32(entry.seats[i]);
    }
  }

  private static fromBytes_v2(reader: BinaryReader): ISeatSelectionEntry[] {
    const entries: ISeatSelectionEntry[] = [];
    const numberOfEntries = reader.readInt32();

    for(let i = 0; i < numberOfEntries; i++) {
      entries.push(this.readSeatSelectionEntry(reader, { passLocks: false }));
    }

    return entries;
  }

  private static fromBytes_v3(reader: BinaryReader): ISeatSelectionEntry[] {
    const entries: ISeatSelectionEntry[] = [];
    const numberOfEntries = reader.readInt32();

    for(let i = 0; i < numberOfEntries; i++) {
      entries.push(this.readSeatSelectionEntry(reader, { passLocks: true }));
    }

    return entries;
  }

  private static fromBytes(reader: BinaryReader): ISeatSelectionEntry[] {
    const entries: ISeatSelectionEntry[] = [];
    const numberOfEntries = reader.readInt32();

    for(let i = 0; i < numberOfEntries; i++) {
      entries.push(this.readSeatSelectionEntry(reader, { passLocks: true, expirationDate: true }));
    }

    return entries;
  }

  static readSeatSelectionEntry(reader: BinaryReader, features: SeatSelectionFeatures): ISeatSelectionEntry {
    const typeId = reader.readByte();

    switch(typeId) {
      case SeatSelectionSerializer.TEMPORARY_SEAT_SELECTION_ENTRY_TYPE_ID:
        return this.readTemporarySeatSelectionEntry(reader, features);
      case SeatSelectionSerializer.PERMANENT_SEAT_SELECTION_ENTRY_TYPE_ID:
        return this.readPermanentSeatSelectionEntry(reader, features);
      case SeatSelectionSerializer.RESERVED_SEAT_SELECTION_ENTRY_TYPE_ID:
        return this.readReservedSeatSelectionEntry(reader, features);
      case SeatSelectionSerializer.PASS_SEAT_SELECTION_ENTRY_TYPE_ID:
        return this.readPassSeatSelectionEntry(reader, features);
      case SeatSelectionSerializer.ARTIFICIAL_SEAT_SELECTION_ENTRY_TYPE_ID:
        return this.readArtificialSeatSelectionEntry(reader, features);
      default:
        throw new SerializationException(`Seat selection entry cannot be deserialized because type ID ${typeId} is unknown`);
    }
  }

  static readTemporarySeatSelectionEntry(reader: BinaryReader, features: SeatSelectionFeatures): TemporarySeatSelectionEntry {
    const sessionId = this.readUniqueId(reader, DatabaseIdGenerator.kAuthIdLength);
    const creationDate = reader.readInt32();
    const expirationDate = reader.readInt32();
    const numberOfSeats = reader.readInt32();
    const seats: number[] = [];

    for(let i = 0; i < numberOfSeats; i++) {
      seats.push(reader.readInt32());
    }

    return new TemporarySeatSelectionEntry(
        sessionId,
        seats,
        dayjs.unix(creationDate),
        dayjs.unix(expirationDate),
    );
  }

  static readPermanentSeatSelectionEntry(reader: BinaryReader, features: SeatSelectionFeatures): PermanentSeatSelectionEntry {
    const sessionId = this.readUniqueId(reader, DatabaseIdGenerator.kAuthIdLength);
    const creationDate = reader.readInt32();
    const numberOfSeats = reader.readInt32();
    const seats: number[] = [];

    for(let i = 0; i < numberOfSeats; i++) {
      seats.push(reader.readInt32());
    }

    return new PermanentSeatSelectionEntry(
        sessionId,
        seats,
        dayjs.unix(creationDate),
    );
  }

  static readReservedSeatSelectionEntry(reader: BinaryReader, features: SeatSelectionFeatures): ReservedSeatSelectionEntry {
    const sessionId = this.readUniqueId(reader, DatabaseIdGenerator.kAuthIdLength);
    const reservationId = this.readUniqueId(reader, DatabaseIdGenerator.kAuthIdLength);
    const creationDate = reader.readInt32();
    const expirationDate = (features.expirationDate === true) ? reader.readInt32() : 0;
    const numberOfSeats = reader.readInt32();
    const seats: number[] = [];

    for(let i = 0; i < numberOfSeats; i++) {
      seats.push(reader.readInt32());
    }

    return new ReservedSeatSelectionEntry(
        sessionId,
        seats,
        dayjs.unix(creationDate),
        expirationDate > 0 ? dayjs.unix(expirationDate) : null,
        reservationId,
    );
  }

  static readPassSeatSelectionEntry(reader: BinaryReader, features: SeatSelectionFeatures): PassSeatSelectionEntry {
    const templateId = this.readUniqueId(reader, DatabaseIdGenerator.kAuthIdLength);
    const creationDate = reader.readInt32();
    const numberOfSeats = reader.readInt32();
    const seats: number[] = [];
    const locks: number[] = [];

    for(let i = 0; i < numberOfSeats; i++) {
      seats.push(reader.readInt32());
    }

    if(features.passLocks) {
      const numberOfLocks = reader.readInt32();
      for(let i = 0; i < numberOfLocks; i++) {
        locks.push(reader.readInt32());
      }
    }

    return PassSeatSelectionEntry.create({
      templateId: templateId,
      seats: seats,
      locks: locks,
      creationDate: dayjs.unix(creationDate),
    });
  }

  static readArtificialSeatSelectionEntry(reader: BinaryReader, features: SeatSelectionFeatures): ArtificialSeatSelectionEntry {
    const creationDate = reader.readInt32();
    const numberOfSeats = reader.readInt32();
    const seats: number[] = [];

    for(let i = 0; i < numberOfSeats; i++) {
      seats.push(reader.readInt32());
    }

    return ArtificialSeatSelectionEntry.create({
      seats: seats,
      creationDate: dayjs.unix(creationDate),
    });
  }

  private static fromBytes_v1(reader: BinaryReader): ISeatSelectionEntry[] {
    const entries: ISeatSelectionEntry[] = [];
    const numberOfEntries = reader.readByte();

    for(let i = 0; i < numberOfEntries; i++) {
      entries.push(this.readSeatSelectionEntry_v1(reader));
    }

    return entries;
  }

  static readSeatSelectionEntry_v1(reader: BinaryReader): ISeatSelectionEntry {
    const sessionId = this.readUniqueId(reader, DatabaseIdGenerator.kAutoIdLength);
    const creationDate = reader.readInt32();
    const expirationDate = reader.readInt32();
    const numberOfSeats = reader.readInt32();
    const seats: number[] = [];

    for(let i = 0; i < numberOfSeats; i++) {
      seats.push(reader.readInt32());
    }

    return new TemporarySeatSelectionEntry(
        sessionId,
        seats,
        dayjs.unix(creationDate),
        dayjs.unix(expirationDate),
    );
  }

  static writeUniqueId(writer: BinaryWriter, uniqueId: string, maxLength: number) {
    if(uniqueId.length > maxLength) {
      throw new SerializationException(
          `Unique ID cannot be serialized because its length of ${uniqueId.length} exceeds the maximum of ${maxLength}.`,
      );
    }

    let length = 0;

    for(let i = 0; i < uniqueId.length; i++) {
      writer.writeByte(uniqueId.charCodeAt(i));
      length++;
    }

    while(length < maxLength) {
      writer.writeByte(0);
      length++;
    }
  }

  static readUniqueId(reader: BinaryReader, maxLength: number): string {
    const charCodes: number[] = [];
    for(let i = 0; i < maxLength; i++) {
      const code = reader.readByte();
      if(code > 0) {
        charCodes.push(code);
      }
    }

    return String.fromCharCode(...charCodes);
  }
}
